import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Sparkles } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const ContactPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'contact@easycoaching.be',
      href: 'mailto:contact@easycoaching.be',
    },
    {
      icon: Phone,
      label: 'Téléphone',
      value: '+32 472 80 27 32',
      href: 'tel:+32472802732',
    },
    {
      icon: MapPin,
      label: 'Localisation',
      value: 'Belgique',
      href: null,
    },
    {
      icon: Clock,
      label: 'Réponse',
      value: 'Sous 24h',
      href: null,
    },
  ];

  const faqs = [
    {
      question: 'Combien de temps dure le premier échange ?',
      answer: 'Le premier appel dure environ 30 minutes. C\'est un échange simple et direct pour comprendre votre situation et vos objectifs.',
    },
    {
      question: 'Est-ce que l\'accompagnement est en ligne ou en présentiel ?',
      answer: 'Les deux sont possibles selon votre situation et vos préférences. Nous en discutons lors du premier échange.',
    },
    {
      question: 'Pour qui est fait cet accompagnement ?',
      answer: 'Pour les personnes et entreprises prêt(e)s à s\'engager réellement dans une démarche structurée et durable. Les places sont volontairement limitées.',
    },
    {
      question: 'Combien coûte l\'accompagnement ?',
      answer: 'Le tarif dépend de votre situation et de vos objectifs. Nous en discutons après avoir analysé votre contexte lors du premier échange.',
    },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#050505] relative overflow-hidden">
        <motion.div
          className="absolute top-0 left-1/3 w-[600px] h-[600px] rounded-full bg-sage/5 blur-[150px]"
          animate={{ 
            x: [0, 100, 0],
            y: [0, 50, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sage/10 border border-sage/20 mb-6"
            >
              <Sparkles size={16} className="text-sage" />
              <span className="font-body text-sm tracking-wide text-sage">
                Contact
              </span>
            </motion.div>
            
            <motion.h1 
              className="font-heading text-5xl md:text-6xl font-medium text-white mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Parlons de <span className="text-sage">votre situation</span>
            </motion.h1>
            
            <motion.p 
              className="font-body text-lg text-gray-400 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Un premier échange simple et direct. 30 minutes pour comprendre 
              où vous en êtes et ce que vous cherchez.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div {...fadeUp} className="lg:col-span-4">
              <h2 className="font-heading text-2xl md:text-3xl text-white mb-8">
                Coordonnées
              </h2>

              <motion.div 
                className="space-y-6 mb-12"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {contactInfo.map((item, index) => (
                  <motion.div 
                    key={item.label} 
                    className="flex items-start gap-4"
                    variants={staggerItem}
                    whileHover={{ x: 8 }}
                  >
                    <motion.div 
                      className="w-12 h-12 rounded-xl bg-sage/10 flex items-center justify-center flex-shrink-0"
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(143, 188, 143, 0.2)' }}
                    >
                      <item.icon className="text-sage" size={22} />
                    </motion.div>
                    <div>
                      <p className="font-body text-sm text-gray-500 mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          data-testid={`contact-info-${item.label.toLowerCase()}`}
                          className="font-body text-white hover:text-sage transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-body text-white">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Engagement Statement */}
              <motion.div 
                className="p-6 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                whileHover={{ y: -5 }}
              >
                <h3 className="font-heading text-lg text-white mb-3">
                  Mon engagement
                </h3>
                <p className="font-body text-gray-400 text-sm leading-relaxed mb-4">
                  Je ne réponds pas à tout le monde. Mais si je vous réponds, 
                  c'est que je pense sincèrement pouvoir vous aider.
                </p>
                <p className="font-body text-sm text-sage italic">
                  "Un accompagnement demande un réel engagement – des deux côtés."
                </p>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <div className="lg:col-span-8">
              <ContactForm
                contactType="general"
                title="Votre demande"
                subtitle="Décrivez votre situation et vos objectifs. Plus vous êtes précis, plus notre échange sera pertinent."
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-[#050505] relative overflow-hidden">
        <motion.div
          className="absolute -right-32 bottom-0 w-[400px] h-[400px] rounded-full bg-mint/5 blur-[100px]"
          animate={{ y: [0, -50, 0] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Questions fréquentes
            </h2>
          </motion.div>

          <motion.div 
            className="space-y-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                whileHover={{ y: -5, scale: 1.01 }}
                className="p-6 rounded-2xl bg-[#0F0F0F] border border-white/5 hover:border-sage/20 transition-all duration-500"
              >
                <h3 className="font-heading text-lg text-white mb-3">
                  {faq.question}
                </h3>
                <p className="font-body text-gray-400">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
